import { useEffect, useState } from 'react';
import type { PermissionLevel, SendControl } from '../shared/api';
import { Banner, Button, Card, DataTable, Field, Input, Modal, Pill, Select, Spinner, Td } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { fmtDate, PERMISSION_LABELS } from '../lib/format';
import { useSession } from '../lib/session';
import { useAsync } from '../lib/useAsync';

export interface TeamMember {
  id: string;
  supabaseUserId: string;
  displayName: string | null;
  email: string | null;
  invitedBy: string | null;
  joinedAt: string | null;
  permissionLevel: PermissionLevel | null;
  pending: boolean;
  isSelf: boolean;
  /** Dual-control (Run 8). Optional so pre-Run-8 fixtures still typecheck. */
  spendCapCents?: number | null;
  sendControl?: SendControl;
}

const LEVELS: PermissionLevel[] = ['read_only', 'field', 'manager', 'admin'];

const LEVEL_HELP: Record<PermissionLevel, string> = {
  admin: 'Everything, including the team roster.',
  manager: 'Sends mail, signs waivers, files liens, reviews approvals.',
  field: 'Mobile app only — logs work and requests approvals.',
  read_only: 'Sees jobs and deadlines; changes nothing.',
};

/** Same four seats, sale-side jobs (Run 9). Agents never send NOOs. */
const BROKERAGE_LEVEL_HELP: Record<PermissionLevel, string> = {
  admin: 'Managing broker / broker of record — everything, including the roster.',
  manager: 'Transaction desk — may invite agents to this brokerage.',
  field: 'Listing agent — works their own files, cannot oversee other agents.',
  read_only: 'Compliance / owner watching — sees the files, changes nothing.',
};

const CONTROL_HELP: Record<SendControl, string> = {
  off: 'Their seat decides everything — today’s behavior.',
  notify: 'They send as normal; you get an in-app and email record of every send.',
  approve: 'Their Send button stays, but nothing fires until an admin approves it.',
};

/** Dollars in, cents out on blur; blank = no cap. */
function CapInput({ member, onCommit }: { member: TeamMember; onCommit: (m: TeamMember, dollars: string) => void }) {
  const initial = member.spendCapCents != null ? String(member.spendCapCents / 100) : '';
  const [value, setValue] = useState(initial);
  useEffect(() => setValue(initial), [initial]);
  return (
    <Input
      aria-label={`Spend cap for ${member.email ?? member.supabaseUserId}`}
      placeholder="No cap"
      inputMode="decimal"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        if (value.trim() !== initial) onCommit(member, value);
      }}
      className="w-24 py-1 text-xs"
    />
  );
}

export default function Team() {
  const { contractor } = useSession();
  // Run 9: brokerages reuse the roster but hide dual-control — those controls
  // gate NOO sends and waiver spend, and agents send neither.
  const brokerage = contractor?.companyKind === 'brokerage';
  const levelHelp = brokerage ? BROKERAGE_LEVEL_HELP : LEVEL_HELP;
  const { data, loading, error, reload } = useAsync(() => api.get<{ team: TeamMember[] }>('/api/team'), []);
  const [inviting, setInviting] = useState(false);
  const [invite, setInvite] = useState({ email: '', displayName: '', permissionLevel: 'field' as PermissionLevel });
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const sendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setActionError(null);
    try {
      const res = await api.post<{ emailed: boolean; joinUrl: string }>('/api/team/invite', {
        email: invite.email,
        displayName: invite.displayName || null,
        permissionLevel: invite.permissionLevel,
      });
      setInviting(false);
      setInvite({ email: '', displayName: '', permissionLevel: 'field' });
      setNotice(res.emailed ? `Invite emailed to ${invite.email}.` : `Seat reserved. Send them this link: ${res.joinUrl}`);
      await reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not send the invite');
    } finally {
      setBusy(false);
    }
  };

  const changeLevel = async (member: TeamMember, level: PermissionLevel) => {
    setActionError(null);
    setNotice(null);
    try {
      await api.put(`/api/team/${encodeURIComponent(member.supabaseUserId)}/permission`, { permissionLevel: level });
      setNotice(`${member.email ?? 'Member'} is now ${PERMISSION_LABELS[level].toLowerCase()}.`);
      await reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not change the permission level');
    }
  };

  const changeControls = async (member: TeamMember, patch: { sendControl?: SendControl; spendCapCents?: number | null }) => {
    setActionError(null);
    setNotice(null);
    try {
      await api.put(`/api/team/${encodeURIComponent(member.supabaseUserId)}/controls`, patch);
      setNotice(`Dual-control updated for ${member.email ?? 'member'}.`);
      await reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not update dual-control');
    }
  };

  const commitCap = (member: TeamMember, dollars: string) => {
    const trimmed = dollars.trim();
    if (trimmed === '') {
      void changeControls(member, { spendCapCents: null });
      return;
    }
    const n = Number(trimmed.replace(/^\$/, ''));
    if (!Number.isFinite(n) || n < 0) {
      setActionError('Spend cap must be a dollar amount, or blank for no cap.');
      return;
    }
    void changeControls(member, { spendCapCents: Math.round(n * 100) });
  };

  const remove = async (member: TeamMember) => {
    if (!window.confirm(`Remove ${member.email ?? 'this member'} from ${contractor?.companyName}?`)) return;
    setActionError(null);
    try {
      await api.del(`/api/team/${encodeURIComponent(member.supabaseUserId)}`);
      setNotice('Member removed.');
      await reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not remove the member');
    }
  };

  if (loading) return <Spinner label="Loading your team…" />;
  if (error) return <Banner tone="urgent" title="Could not load the team">{error}</Banner>;

  const team = data?.team ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-ink">Team</h1>
          <p className="text-sm text-muted">Who can act for {contractor?.companyName}, and at what level.</p>
          <p className="text-sm text-muted">
            {brokerage
              ? 'Admin is the managing broker. Manager runs the transaction desk and may invite agents. Field is a listing agent — they cannot oversee other agents. Read-only watches.'
              : 'Manager and admin can send notices, sign waivers, and file. Field logs work and requests approval. Read-only sees the file.'}
          </p>
        </div>
        <Button onClick={() => setInviting(true)}>Invite by email</Button>
      </div>

      {notice && <Banner tone="ok">{notice}</Banner>}
      {actionError && <Banner tone="urgent">{actionError}</Banner>}

      {team.length === 1 && team[0].isSelf && (
        <Banner tone="watch" title="Add the people who send notices">
          Each person gets a seat on this office — not a new company.
        </Banner>
      )}

      <Card>
        <DataTable
          head={brokerage ? ['Name', 'Email', 'Level', 'Joined', ''] : ['Name', 'Email', 'Level', 'Dual-control', 'Joined', '']}
          empty="Nobody on the roster yet."
        >
          {team.map((m) => (
            <tr key={m.id} className="border-b border-border/60">
              <Td className="font-medium">
                {m.displayName ?? '—'}
                {m.isSelf && <span className="ml-2 text-xs text-dim">(you)</span>}
              </Td>
              <Td className="text-muted">{m.email ?? '—'}</Td>
              <Td>
                <div className="flex items-center gap-2">
                  <Select
                    aria-label={`Permission level for ${m.email ?? m.supabaseUserId}`}
                    value={m.permissionLevel ?? 'field'}
                    onChange={(e) => void changeLevel(m, e.target.value as PermissionLevel)}
                    className="w-36 py-1 text-xs"
                  >
                    {LEVELS.map((l) => (
                      <option key={l} value={l}>
                        {PERMISSION_LABELS[l]}
                      </option>
                    ))}
                  </Select>
                  {m.pending && <Pill tone="watch">Invite pending</Pill>}
                </div>
              </Td>
              {!brokerage && (
                <Td>
                  {m.isSelf ? (
                    <span className="text-xs text-dim">—</span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Select
                        aria-label={`Send control for ${m.email ?? m.supabaseUserId}`}
                        value={m.sendControl ?? 'off'}
                        onChange={(e) => void changeControls(m, { sendControl: e.target.value as SendControl })}
                        className="w-32 py-1 text-xs"
                      >
                        <option value="off">Off</option>
                        <option value="notify">Notify me</option>
                        <option value="approve">Approve first</option>
                      </Select>
                      <CapInput member={m} onCommit={commitCap} />
                    </div>
                  )}
                </Td>
              )}
              <Td className="text-muted">{m.pending ? 'Invited' : fmtDate(m.joinedAt)}</Td>
              <Td>
                {!m.isSelf && (
                  <Button variant="ghost" onClick={() => void remove(m)} className="px-2 py-1 text-xs text-urgent">
                    Remove
                  </Button>
                )}
              </Td>
            </tr>
          ))}
        </DataTable>
      </Card>

      <Card title="What each level can do">
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {LEVELS.slice().reverse().map((l) => (
            <div key={l} className="rounded-lg border border-border bg-surface-alt/40 px-4 py-3">
              <dt className="text-sm font-semibold text-ink">{PERMISSION_LABELS[l]}</dt>
              <dd className="mt-0.5 text-xs text-muted">{levelHelp[l]}</dd>
            </div>
          ))}
        </dl>
      </Card>

      {!brokerage && (
        <Card title="Dual-control (optional)">
          <p className="mb-3 text-xs text-muted">
            Approve before they send or sign — the seat stays the floor, these only tighten it. The cap holds any spend
            over it for your approval, whatever the send control. Blank cap = no cap.
          </p>
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {(['off', 'notify', 'approve'] as SendControl[]).map((c) => (
              <div key={c} className="rounded-lg border border-border bg-surface-alt/40 px-4 py-3">
                <dt className="text-sm font-semibold text-ink">{c === 'off' ? 'Off' : c === 'notify' ? 'Notify me' : 'Approve first'}</dt>
                <dd className="mt-0.5 text-xs text-muted">{CONTROL_HELP[c]}</dd>
              </div>
            ))}
          </dl>
        </Card>
      )}

      {inviting && (
        <Modal title="Invite a teammate" onClose={() => setInviting(false)}>
          <form onSubmit={sendInvite} className="space-y-4">
            <Field label="Work email">
              <Input required type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
            </Field>
            <Field label="Name (optional)">
              <Input value={invite.displayName} onChange={(e) => setInvite({ ...invite, displayName: e.target.value })} />
            </Field>
            <Field label="Office seat">
              <Select
                value={invite.permissionLevel}
                onChange={(e) => setInvite({ ...invite, permissionLevel: e.target.value as PermissionLevel })}
              >
                {LEVELS.slice().reverse().map((l) => (
                  <option key={l} value={l}>
                    {PERMISSION_LABELS[l]}
                  </option>
                ))}
              </Select>
            </Field>
            <p className="text-xs text-muted">{levelHelp[invite.permissionLevel]}</p>
            <p className="text-xs text-muted">
              They get a sign-in link and join {contractor?.companyName} at this seat. If you skip the choice, new
              teammates start as Field — the safest seat.
            </p>
            {actionError && <Banner tone="urgent">{actionError}</Banner>}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setInviting(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? 'Adding…' : 'Add to this office'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
